import { SelectDateTime } from "../../../../components/date-picker/date-picker";
import Buttons from "../../../../components/button";
import { FiPlus, FiTrash } from "react-icons/fi";
import {
  JobAppEducationHistoryResponseDegree,
  useGetAllUniversityDegrees,
} from "../../../api-docs";
import { useMemo } from "react";
import { UniversityDegreeTypeLabel } from "../../../../components/types/university-degree-type";
import { AutoOpenVirtualizedSelectView } from "../../../../components/select/auto-open-virtualized-select";
import { MultiFieldSet } from "@/components/utils/useFieldSet";
import { EducationHistoryFields } from "modules/self-apply/validation/candidate-self-apply";
import { ValidationMessage } from "@/flokit/core/validation-message/validation-message";

const InitialValueFactory = () => ({
  degree: "" as JobAppEducationHistoryResponseDegree,
  university: "",
  endDate: "",
});

export const EducationHistoryFragment = (
  props: {
    isRequired?: boolean;
    degreeTypeRequired?: boolean;
    universityRequired?: boolean;
    endDateRequired?: boolean;
  } & MultiFieldSet<EducationHistoryFields>,
) => {
  const degreeResponse = useGetAllUniversityDegrees();
  const degrees = useMemo(() => {
    return (degreeResponse.data ?? {}).universityDegrees ?? {};
  }, [degreeResponse.data]);

  const degreeOptions = Object.keys(degrees).map((degreeEnum) => {
    const label = UniversityDegreeTypeLabel.get(parseInt(degreeEnum, 10));
    const value = degreeEnum;
    return { label, value };
  });

  const info = props;

  return (
    <>
      <div className="divide-dublin-gray flex flex-col divide-y">
        {info?.values?.map((historyItem, index) => (
          <div key={index} className="py-5 first:py-0 first:pb-5">
            <div className="relative grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2">
              <div className="md:col-span-2">
                <AutoOpenVirtualizedSelectView
                  required={props.degreeTypeRequired}
                  options={degreeOptions}
                  validationMsg={info.validationMsg?.[index]?.degree}
                  shouldShowValidation={info.validation?.[index]?.degree}
                  name="degreeType"
                  label="Degree Type"
                  onSelectItem={(item) => {
                    return info.setValue(index, "degree", item.value);
                  }}
                  // onBlur
                  placeholder="Select Degree Type"
                  isItemSelected={(option) => {
                    return (
                      parseInt(option.value, 10) ===
                      parseInt(historyItem.degree ?? "", 10)
                    );
                  }}
                />
              </div>
              <AutoOpenVirtualizedSelectView
                required={props.universityRequired}
                searchable={true}
                options={(degrees[historyItem.degree] ?? []).map((school) => ({
                  label: school,
                  value: school,
                }))}
                shouldShowValidation={info.validation?.[index]?.university}
                validationMsg={info.validationMsg?.[index]?.university}
                name="university"
                label="School"
                onSelectItem={(item) => {
                  return info.setValue(index, "university", item.value);
                }}
                // onBlur
                isItemSelected={(i) => i.value === historyItem.university}
                placeholder="Select School"
              />
              <SelectDateTime
                required={props.endDateRequired}
                name="graduationDate"
                isValid={!info.validation?.[index]?.endDate}
                validationMessage={info.validationMsg?.[index]?.endDate}
                label="Graduation Date"
                onBlur={info.onBlur("endDate")}
                onChange={(e) => {
                  return info.setValue(index, "endDate", e.currentTarget.value);
                }}
                type="date"
                value={historyItem.endDate}
              />
            </div>
            <div className="mt-5 flex justify-end">
              <Buttons.Micro
                className="hover:text-destructive flex  gap-x-2"
                variant="ghost"
                onClick={() => info.removeItem(index)}
              >
                <FiTrash size={14} />
                <span>Remove Entry</span>
              </Buttons.Micro>
            </div>
          </div>
        ))}
      </div>
      <div>
        <Buttons.Micro
          onClick={() => {
            info.addItem(InitialValueFactory());
          }}
          className="flex gap-x-2"
          variant="secondary"
        >
          <FiPlus />
          <span>Add Another School</span>
        </Buttons.Micro>
      </div>
      {props.isRequired && !info.hasAllValid && info.hasAnyDirty ? (
        <div className="pt-4">
          <ValidationMessage
            shouldShowValidation
            validationMessage="Please add at least one education history entry"
          />
        </div>
      ) : null}
    </>
  );
};
